/**
 * Dynamic-block DETECTION (Track A, fix-dxf-dynamic).
 *
 * Extracted from dxfBridge.ts and dxfBridge.dynamic.ts so the 2000-line
 * guardrail on the main bridge stays intact (CLAUDE.md: ">2000 не
 * допускать"). The detection logic is pure and self-contained: it walks
 * BLOCK_RECORD entries, OBJECTS dictionaries, and the pre-parsed
 * dynamic-block objects list to decide whether each block definition is
 * dynamic. It does NOT populate `dynamicMetadata` — that is Track B /
 * DYN-1.5's job (see dxfBridge.dynamic.parser.ts) and requires a
 * full OBJECTS eval-graph decode that the incremental AutoCAD releases
 * keep extending. Detection is independent on purpose: the UI can flag
 * dynamic blocks (and avoid the misleading "static" label) even when
 * the eval graph is beyond our current Tier.
 *
 * ── Markers used ──
 *
 * A block is dynamic iff ANY of the following is true for its
 * BLOCK_RECORD entry or BLOCK handle (AutoCAD 2007+ conventions,
 * confirmed against the АБИ1.dxf fixture and the DXF reference):
 *
 *   1. BLOCK_RECORD has an `ACAD_XDICTIONARY` extension dictionary
 *      (group 102 `{ACAD_XDICTIONARY` … 360 <handle> … 102 `}`) whose
 *      referenced DICTIONARY owns an `ACAD_ENHANCEDBLOCK` child
 *      (the AutoCAD 2007+ container for the eval-graph /
 *      ACDBEVALGRAPH representation). This is the canonical marker.
 *
 *   2. BLOCK_RECORD has XData emitted under the well-known authoring
 *      app names `AcDbBlockRepETag`, `AcDbDynamicBlockTrueName` or
 *      `AcDbDynamicBlockGUID`. AutoCAD writes these on every dynamic
 *      master block record for round-trip integrity. The *TrueName*
 *      entry carries the display name of the dynamic master for
 *      anonymous `*U<N>` variants.
 *
 *   3. The OBJECTS section contains `ACDBBLOCKREPRESENTATION`,
 *      `ACDBEVALGRAPH` or any `ACDBBLOCK*PARAMETER` / `ACDBBLOCK*ACTION`
 *      (Tier-1..N) whose `ownerHandle` matches either the BLOCK handle
 *      or the BLOCK_RECORD handle. This is the authoritative signal
 *      once the eval graph parser is available; it also catches the
 *      rare "master with no XDICTIONARY but with an eval graph"
 *      encoding from older BricsCAD exports.
 *
 * Any one of 1/2/3 is enough — ad hoc DXF authors sometimes omit 1, and
 * AutoCAD LT trims 2, so we accept the union.
 *
 * Note: the legacy anonymous-block flag (group 280 = 1 on BLOCK_RECORD)
 * is NOT a reliable dynamic marker. AutoCAD sets it for anonymous
 * variants of a dynamic master (`*U<N>`), for dimension/hatch blocks,
 * and for associative-array source blocks — all of which include
 * statically-rendered blocks with no eval graph.
 */
import type {
  DxfDictionaryEntry,
  DxfDocument,
  DxfDynamicBlockObject,
  DxfObjectsSection,
  DxfTablesSection,
  DxfXDataEntry,
} from "@dxf-ai-editor/dxf-io";
import { getObjectsSection, getTablesSection } from "@dxf-ai-editor/dxf-io";

import type { BlockDefinition } from "./model";
import { canonicalDynamicBlockClass } from "./dxfBridge.dynamic.shared";

// ── Shared constants ───────────────────────────────────────────────

// DICTIONARY-entry name emitted by AutoCAD 2007+ on the dynamic-block
// extension dictionary. The case must match exactly — ezdxf and all
// modern AutoCAD releases write it as below.
const ENHANCED_BLOCK_ENTRY = "ACAD_ENHANCEDBLOCK";

// XData application names AutoCAD attaches to every dynamic BLOCK_RECORD.
// Set semantics: presence of ANY is sufficient. Kept as a Set so the
// check is O(1) per entry.
const DYNAMIC_XDATA_APP_NAMES = new Set<string>([
  "AcDbBlockRepETag",
  "AcDbDynamicBlockTrueName",
  "AcDbDynamicBlockGUID",
]);

// OBJECTS-section classes that imply an eval graph / dynamic-block
// authoring structure. Parameters / actions whose class name starts
// with `ACDBBLOCK*PARAMETER` or `ACDBBLOCK*ACTION` are also covered via
// the prefix test below.
const DYNAMIC_EVAL_CLASSES = new Set<string>([
  "ACDBBLOCKREPRESENTATION",
  "ACDBEVALGRAPH",
]);

function looksLikeDynamicEvalClass(type: string): boolean {
  // Canonicalise first so AutoCAD 2018+ un-prefixed spellings
  // (BLOCKLINEARPARAMETER, BLOCKHORIZONTALCONSTRAINTPARAMETER, …) match
  // the prefixed pattern checks below.
  const upper = canonicalDynamicBlockClass(type.trim().toUpperCase());
  if (DYNAMIC_EVAL_CLASSES.has(upper)) return true;
  // Parameter / action classes (Tier 1..N). Prefix test keeps the check
  // robust against future-tier extensions that we have not yet listed
  // explicitly — anything in the ACDBBLOCK* / ACDBDYN* families is
  // authoring state that only exists on dynamic blocks.
  if (/^ACDBBLOCK.*PARAMETER$/.test(upper)) return true;
  if (/^ACDBBLOCK.*ACTION$/.test(upper)) return true;
  if (upper === "ACDBDYNAMICBLOCKPURGEPREVENTER") return true;
  return false;
}

function hasDynamicXData(xdata: DxfXDataEntry[] | undefined): boolean {
  if (!xdata || xdata.length === 0) return false;
  for (const xd of xdata) {
    if (DYNAMIC_XDATA_APP_NAMES.has(xd.appName)) return true;
  }
  return false;
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Return a new `blocks` map with `dynamicBlock: true` set on every
 * block that matches any of the detection markers documented in the
 * file header. Input map is not mutated.
 *
 * Preconditions: `blocks` is already populated by
 * `importBlockDefinitions` and `attachDynamicBlockMetadata` has
 * already run (so `dynamicMetadata`, if any, is present — we OR that
 * in too so Track B upgrades flow through automatically).
 *
 * @param document     parsed DXF document (source of truth for OBJECTS +
 *                     TABLES sections)
 * @param blocks       block-name → BlockDefinition map, typically the
 *                     output of `attachDynamicBlockMetadata`
 * @returns            fresh map with `dynamicBlock` flag filled in
 */
export function detectDynamicBlocks(
  document: DxfDocument,
  blocks: Record<string, BlockDefinition>,
): Record<string, BlockDefinition> {
  const objectsSection = getObjectsSection(document);
  const tablesSection = getTablesSection(document);
  return detectDynamicBlocksFromSections(tablesSection, objectsSection, blocks);
}

/**
 * Section-level variant used by unit tests that build synthetic
 * sections directly instead of going through `parseDxfDocument`.
 * Keeps the function pure and self-contained for testing.
 */
export function detectDynamicBlocksFromSections(
  tablesSection: DxfTablesSection | null,
  objectsSection: DxfObjectsSection | null,
  blocks: Record<string, BlockDefinition>,
): Record<string, BlockDefinition> {
  // Nothing to detect? Still return a fresh object so the caller can
  // treat the result as owned.
  const out: Record<string, BlockDefinition> = {};
  for (const [name, def] of Object.entries(blocks)) {
    out[name] = { ...def };
  }

  const blockRecords = tablesSection?.blockRecords ?? [];
  // Map BLOCK_RECORD name → BLOCK_RECORD entry so we can pick by the
  // drawing-model BlockDefinition.name (which mirrors BLOCK name, which
  // in turn mirrors BLOCK_RECORD name — AutoCAD keeps them synced).
  const recordsByName = new Map<string, (typeof blockRecords)[number]>();
  for (const rec of blockRecords) {
    if (rec.name) recordsByName.set(rec.name, rec);
  }

  // Map DICTIONARY handle → has ACAD_ENHANCEDBLOCK child? AutoCAD only
  // writes the entry name on dynamic blocks, so a `true` here is a
  // definitive dynamic marker for whatever BLOCK_RECORD points at the
  // DICTIONARY through its xdictionary handle.
  const enhancedByDictHandle = new Map<string, boolean>();
  if (objectsSection) {
    for (const dict of objectsSection.dictionaries) {
      if (dictionaryContainsEnhancedBlock(dict)) {
        enhancedByDictHandle.set(dict.handle, true);
      }
    }
  }

  // Map owner handle → bool: does at least one OBJECTS dynamic-block
  // record own by this handle? BLOCK_RECORDs own ACDBBLOCKREPRESENTATION /
  // ACDBEVALGRAPH records via the `ownerHandle` back-reference; BLOCK
  // headers can legitimately own in older BricsCAD exports too.
  const evalOwnerHandles = new Set<string>();
  if (objectsSection) {
    for (const obj of objectsSection.dynamicBlockObjects) {
      if (!obj.ownerHandle) continue;
      if (looksLikeDynamicEvalClass(obj.type)) {
        evalOwnerHandles.add(obj.ownerHandle);
      }
    }
  }

  for (const [name, def] of Object.entries(out)) {
    // Preserve Track B results: if the parser already produced typed
    // metadata, the block is dynamic regardless of what the other
    // markers say.
    if (def.dynamicMetadata) {
      out[name] = { ...def, dynamicBlock: true };
      continue;
    }

    const record = recordsByName.get(name);
    const recordHandle = record?.handle ?? "";
    const blockHandle = def.handle ?? "";

    let isDynamic = false;

    // Marker 1 — xdictionary with ACAD_ENHANCEDBLOCK child.
    if (record?.extensionDictionaryHandle) {
      if (enhancedByDictHandle.get(record.extensionDictionaryHandle)) {
        isDynamic = true;
      }
    }

    // Marker 2 — well-known dynamic-block XData on the BLOCK_RECORD.
    if (!isDynamic && hasDynamicXData(record?.xdata)) {
      isDynamic = true;
    }

    // Marker 3 — OBJECTS-section eval graph / representation / parameter
    // / action pointing back at this block's handle or record handle.
    if (!isDynamic) {
      if (recordHandle && evalOwnerHandles.has(recordHandle)) isDynamic = true;
      else if (blockHandle && evalOwnerHandles.has(blockHandle))
        isDynamic = true;
    }

    if (isDynamic) {
      // Parameter defaults + per-INSERT customProperties seeding is
      // handled by `extractDynamicBlockParams` /
      // `attachInsertCustomProperties` (see dxfBridge.dynamic.params.ts),
      // invoked by importDxfDocumentToDrawingDocument immediately after
      // this detection pass.
      out[name] = { ...def, dynamicBlock: true };
    } else {
      out[name] = { ...def, dynamicBlock: false };
    }
  }

  return out;
}

// ── Helpers ────────────────────────────────────────────────────────

/**
 * True iff the DICTIONARY entry lists a child keyed by the canonical
 * `ACAD_ENHANCEDBLOCK` name. Some AutoCAD releases also drop a
 * `AcDbDynamicBlockRoundTripPurgePreventer` child alongside it — we
 * treat either as equivalent evidence because the purge-preventer is
 * only emitted for dynamic masters.
 */
function dictionaryContainsEnhancedBlock(dict: DxfDictionaryEntry): boolean {
  for (const ref of dict.entries) {
    if (ref.name === ENHANCED_BLOCK_ENTRY) return true;
    if (ref.name === "AcDbDynamicBlockRoundTripPurgePreventer") return true;
  }
  return false;
}

/**
 * Test-only re-export of detection internals (used by *.test.ts via dynamic import patterns).
 * @public
 */
export const __testables = {
  dictionaryContainsEnhancedBlock,
  looksLikeDynamicEvalClass,
  hasDynamicXData,
};

// Ensure the unused-import warning doesn't fire on the type-only import.
export type { DxfDynamicBlockObject };
